'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/components/ui/use-toast';
import { storyKeys } from '../queryKeys';
import { addBlock, updateBlock, deleteBlock, reorderBlocks, setBlockVoice } from '../actions/blocks';
import type { Story } from '../types';

export function useBlockMutations(storyId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
  const { toast } = useToast();
  /** A refused write (the generation lock …) says why instead of doing nothing. */
  const refused = (err: unknown) => {
    void invalidate();
    toast({ variant: 'destructive', description: err instanceof Error ? err.message : String(err) });
  };

  const add = useMutation({
    mutationFn: async (params: { text: string; afterBlockId?: string | null }) => {
      const result = await addBlock(storyId, params.text, params.afterBlockId);
      if (!result.success) throw new Error(result.error);
      return result.data;
    },
    onSuccess: invalidate,
    onError: refused,
  });

  // A refused write (EDIT_FORBIDDEN: the generation lock, an open print order) is an
  // error with the translated reason — the caller's onError shows it.
  const update = useMutation({
    mutationFn: async (params: { blockId: string; text: string }) => {
      const result = await updateBlock(storyId, params.blockId, params.text);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (blockId: string) => {
      const result = await deleteBlock(storyId, blockId);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: async (blockIds: string[]) => {
      const result = await reorderBlocks(storyId, blockIds);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: invalidate,
    onError: refused,
  });

  const setVoice = useMutation({
    mutationFn: (params: { blockId: string; voiceId: string; voiceName: string }) =>
      setBlockVoice(storyId, params.blockId, params.voiceId, params.voiceName),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: storyKeys.detail(storyId) });
      const previous = queryClient.getQueryData<Story>(storyKeys.detail(storyId));
      queryClient.setQueryData<Story>(storyKeys.detail(storyId), (old) => {
        if (!old) return old;
        return {
          ...old,
          textBlocks: old.textBlocks.map((b) =>
            b.id === params.blockId
              ? { ...b, voiceId: params.voiceId, voiceName: params.voiceName }
              : b
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(storyKeys.detail(storyId), context.previous);
      }
    },
  });

  return {
    addBlock: add,
    updateBlock: update,
    deleteBlock: remove,
    reorderBlocks: reorder,
    setBlockVoice: setVoice,
  };
}
